import type { PersonDto } from "@caloteiros/shared";
import { useState } from "react";
import type { ReactElement } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "../components/AppShell";
import { IconPlus, IconX } from "../components/Icons";
import { PersonChips } from "../components/PersonPicker";
import { Field, Spinner } from "../components/ui";
import { ApiError } from "../lib/api";
import { useCreateGroup, usePeople } from "../lib/queries";
import { useToast } from "../lib/toast";

const EMOJIS = ["🧾", "🔥", "🍻", "🏖️", "🍕", "✈️", "🏠", "🎂", "⚽", "🎬"];

export function NewGroupPage(): ReactElement {
  const navigate = useNavigate();
  const toast = useToast();
  const people = usePeople();
  const createGroup = useCreateGroup();

  const [name, setName] = useState("");
  const [emoji, setEmoji] = useState<string>("🧾");
  const [draftName, setDraftName] = useState("");
  const [newNames, setNewNames] = useState<string[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // O proprio usuario ja entra no grupo pelo servidor: nao aparece na lista.
  const contacts: PersonDto[] = (people.data ?? []).filter((person) => !person.isMe);

  const addDraft = (): void => {
    const value = draftName.trim();
    if (!value) return;
    setNewNames((current) => [...current, value]);
    setDraftName("");
  };

  const submit = async (): Promise<void> => {
    if (!name.trim()) {
      toast.error("Dê um nome ao rolê.");
      return;
    }
    try {
      const group = await createGroup.mutateAsync({
        name: name.trim(),
        emoji,
        description: undefined,
        memberNames: newNames,
        memberIds: [...selectedIds],
      });
      navigate(`/app/grupos/${group.id}`, { replace: true });
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "Não consegui criar o rolê.");
    }
  };

  return (
    <>
      <PageHeader title="Novo rolê" back />

      <main className="app-content stack-lg">
        <Field label="Nome do rolê">
          <input
            className="input"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Churrasco de sábado"
            autoFocus
            maxLength={80}
          />
        </Field>

        <Field as="div" label="Ícone">
          <div className="chip-list">
            {EMOJIS.map((option) => (
              <button
                key={option}
                type="button"
                className="chip"
                aria-pressed={emoji === option}
                onClick={() => setEmoji(option)}
                aria-label={`Ícone ${option}`}
              >
                <span style={{ fontSize: "1.1rem" }}>{option}</span>
              </button>
            ))}
          </div>
        </Field>

        <div className="stack">
          <h2 className="section-title">Quem vai</h2>
          <p className="hint">
            Só o nome basta. Se a pessoa criar conta depois, é só vincular — nada do que
            já foi lançado se perde.
          </p>

          <div className="row">
            <input
              className="input grow"
              value={draftName}
              onChange={(event) => setDraftName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  addDraft();
                }
              }}
              placeholder="Nome da pessoa"
              maxLength={80}
            />
            <button type="button" className="btn btn-subtle" onClick={addDraft}>
              <IconPlus width={18} height={18} />
              Add
            </button>
          </div>

          {newNames.length > 0 ? (
            <div className="chip-list">
              {newNames.map((personName, index) => (
                <span key={`${personName}-${index}`} className="chip chip-on">
                  {personName}
                  <button
                    type="button"
                    className="btn-icon"
                    style={{ minHeight: 24, minWidth: 24 }}
                    aria-label={`Remover ${personName}`}
                    onClick={() => setNewNames((current) => current.filter((_, i) => i !== index))}
                  >
                    <IconX width={14} height={14} />
                  </button>
                </span>
              ))}
            </div>
          ) : null}

          {contacts.length > 0 ? (
            <div className="stack" style={{ marginTop: "var(--space-3)" }}>
              <span className="label">Ou puxe do seu caderninho</span>
              <PersonChips
                people={contacts}
                selected={selectedIds}
                onToggle={(personId) =>
                  setSelectedIds((current) => {
                    const next = new Set(current);
                    if (next.has(personId)) next.delete(personId);
                    else next.add(personId);
                    return next;
                  })
                }
              />
            </div>
          ) : null}
        </div>

        <button
          type="button"
          className="btn btn-primary btn-block"
          onClick={() => void submit()}
          disabled={createGroup.isPending}
        >
          {createGroup.isPending ? <Spinner /> : "Criar rolê"}
        </button>
      </main>
    </>
  );
}
